import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Save, Star, Search, ExternalLink, Upload, ImagePlus, GripVertical } from "lucide-react";
import { fetchEpisodes, fetchArticles, adminCreateEpisode, adminUpdateEpisode, adminDeleteEpisode, adminUploadImage } from "../lib/api";

const EMPTY = {
  slug: "",
  number: 1,
  title: "",
  description: "",
  publish_date: "",
  duration: "",
  hero_image: "",
  spotify_episode_id: "",
  youtube_video_id: "",
  guest_name: "",
  guest_bio: "",
  guest_handle: "",
  chapters: [],
  featured_article_slugs: [],
  tags: "",
  featured: false,
};

function parseSecs(str) {
  if (!str) return 0;
  const parts = String(str).split(":").map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function fmtSecs(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function EpisodesPanel({ token }) {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchEpisodes({ limit: 100 });
      setEpisodes(r.episodes || []);
    } catch { toast.error("Couldn't load episodes."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete episode "${slug}"? Can't be undone.`)) return;
    try {
      await adminDeleteEpisode(token, slug);
      toast.success("Episode deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const filtered = episodes.filter((e) =>
    !q ||
    e.title.toLowerCase().includes(q.toLowerCase()) ||
    e.slug.toLowerCase().includes(q.toLowerCase()) ||
    String(e.number).includes(q)
  );

  if (editing !== null) {
    return <EpisodeEditor token={token} initial={editing}
                          onCancel={() => setEditing(null)}
                          onSaved={() => { setEditing(null); load(); }} />;
  }

  return (
    <div data-testid="admin-episodes-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-kicker">The Podcast</div>
          <h2 className="text-headline text-3xl md:text-4xl">Episodes</h2>
          <p className="text-dek mt-1">Publish a new drop. Update chapters. Link to recaps.</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 border-2 border-[var(--ink)] px-3 py-2 bg-[var(--paper)]">
            <Search size={14} className="text-[var(--muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   className="bg-transparent outline-none text-sm w-40 md:w-64"
                   placeholder="Filter…"
                   data-testid="episodes-filter-input" />
          </div>
          <button onClick={() => setEditing({ ...EMPTY, number: (episodes[0]?.number || 0) + 1 })}
                  className="btn-rust" data-testid="episodes-new-button">
            <Plus size={14} /> New Episode
          </button>
        </div>
      </div>

      <div className="border-2 border-[var(--ink)] overflow-hidden" data-testid="episodes-table">
        <div className="grid grid-cols-12 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-widest text-xs px-4 py-3">
          <div className="col-span-1">#</div>
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Duration</div>
          <div className="col-span-2">Players</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">No episodes match.</div>
        ) : (
          <div className="divide-y divide-[var(--line)]">
            {filtered.map((e) => (
              <div key={e.slug} className="grid grid-cols-12 px-4 py-3 text-sm items-center hover:bg-[var(--paper-deep)]/40"
                   data-testid={`episodes-row-${e.slug}`}>
                <div className="col-span-1 font-display text-xl text-[var(--rust)]">{e.number}</div>
                <div className="col-span-5">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {e.featured && <Star size={12} className="text-[var(--rust)] fill-[var(--rust)]" />}
                    {e.title}
                  </div>
                  <div className="text-xs text-[var(--muted)] font-mono-ed truncate">{e.slug}</div>
                </div>
                <div className="col-span-2 text-[var(--muted)] font-mono-ed text-xs">{e.duration || "—"}</div>
                <div className="col-span-2 flex gap-2">
                  {e.spotify_episode_id && <span className="chip text-[0.6rem]">Spotify</span>}
                  {e.youtube_video_id && <span className="chip text-[0.6rem]">YouTube</span>}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <a href={`/episodes/${e.slug}`} target="_blank" rel="noreferrer"
                     className="p-2 hover:text-[var(--rust)]" title="View live"
                     data-testid={`episodes-view-${e.slug}`}>
                    <ExternalLink size={14} />
                  </a>
                  <button onClick={() => setEditing(e)} className="p-2 hover:text-[var(--rust)]"
                          title="Edit" data-testid={`episodes-edit-${e.slug}`}>
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => remove(e.slug)} className="p-2 hover:text-red-700"
                          title="Delete" data-testid={`episodes-delete-${e.slug}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EpisodeEditor({ token, initial, onCancel, onSaved }) {
  const isNew = !initial.slug;
  const [form, setForm] = useState({
    slug: initial.slug || "",
    number: initial.number || 1,
    title: initial.title || "",
    description: initial.description || "",
    publish_date: initial.publish_date?.slice(0, 10) || "",
    duration: initial.duration || "",
    hero_image: initial.hero_image || "",
    spotify_episode_id: initial.spotify_episode_id || "",
    youtube_video_id: initial.youtube_video_id || "",
    guest_name: initial.guest_name || "",
    guest_bio: initial.guest_bio || "",
    guest_handle: initial.guest_handle || "",
    chapters: (initial.chapters || []).map((c) => ({ ...c, _time: fmtSecs(c.time_seconds) })),
    featured_article_slugs: Array.isArray(initial.featured_article_slugs) ? initial.featured_article_slugs.join(", ") : "",
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    featured: !!initial.featured,
  });
  const [articles, setArticles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchArticles({ limit: 100 }).then((r) => setArticles(r.articles || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
            : e.target.type === "number" ? Number(e.target.value) || 0
            : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const updateChapter = (idx, field, value) => {
    const chapters = [...form.chapters];
    chapters[idx] = { ...chapters[idx], [field]: value };
    setForm({ ...form, chapters });
  };
  const addChapter = () => setForm({ ...form, chapters: [...form.chapters, { _time: "0:00", label: "" }] });
  const removeChapter = (idx) => setForm({ ...form, chapters: form.chapters.filter((_, i) => i !== idx) });

  const toggleArticle = (slug) => {
    const arr = form.featured_article_slugs.split(",").map((s) => s.trim()).filter(Boolean);
    const next = arr.includes(slug) ? arr.filter((s) => s !== slug) : [...arr, slug];
    setForm({ ...form, featured_article_slugs: next.join(", ") });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.error("Title and description are required."); return;
    }
    setSaving(true);
    const payload = {
      slug: form.slug,
      number: Number(form.number),
      title: form.title,
      description: form.description,
      publish_date: form.publish_date ? new Date(form.publish_date).toISOString() : undefined,
      duration: form.duration,
      hero_image: form.hero_image,
      spotify_episode_id: form.spotify_episode_id.trim(),
      youtube_video_id: form.youtube_video_id.trim(),
      guest_name: form.guest_name,
      guest_bio: form.guest_bio,
      guest_handle: form.guest_handle,
      chapters: form.chapters
        .filter((c) => c.label)
        .map((c) => ({ time_seconds: parseSecs(c._time || "0:00"), label: c.label })),
      featured_article_slugs: form.featured_article_slugs.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      featured: form.featured,
    };
    try {
      if (isNew) {
        const created = await adminCreateEpisode(token, payload);
        toast.success(`Published. /episodes/${created.slug}`);
      } else {
        await adminUpdateEpisode(token, initial.slug, payload);
        toast.success("Episode updated.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const selectedSlugs = new Set(form.featured_article_slugs.split(",").map((s) => s.trim()).filter(Boolean));

  return (
    <div data-testid="episode-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-kicker">{isNew ? "New Episode" : `Editing Ep. ${initial.number}`}</div>
          <h2 className="text-headline text-3xl md:text-4xl">
            {isNew ? "Drop a New Episode" : form.title || "Untitled"}
          </h2>
        </div>
        <button onClick={onCancel} className="btn-ghost text-xs" data-testid="episode-editor-cancel">
          <X size={14} /> Cancel
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 sticker-card p-6 md:p-8 space-y-5">
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <label className="text-kicker block mb-1">Number *</label>
              <input type="number" className="field" required min="1" value={form.number} onChange={set("number")}
                     data-testid="episode-number-input" />
            </div>
            <div className="sm:col-span-3">
              <label className="text-kicker block mb-1">Title *</label>
              <input className="field" required value={form.title} onChange={set("title")}
                     data-testid="episode-title-input" />
            </div>
          </div>
          <div>
            <label className="text-kicker block mb-1">Slug (auto if blank)</label>
            <input className="field" value={form.slug} onChange={set("slug")}
                   data-testid="episode-slug-input" />
          </div>
          <div>
            <label className="text-kicker block mb-1">Description *</label>
            <textarea className="field min-h-[100px] resize-y" required
                      value={form.description} onChange={set("description")}
                      data-testid="episode-description-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-kicker block mb-1">Publish Date</label>
              <input type="date" className="field" value={form.publish_date} onChange={set("publish_date")}
                     data-testid="episode-date-input" />
            </div>
            <div>
              <label className="text-kicker block mb-1">Duration (mm:ss)</label>
              <input className="field" placeholder="54:12" value={form.duration} onChange={set("duration")}
                     data-testid="episode-duration-input" />
            </div>
          </div>

          <div className="pt-4 border-t-2 border-[var(--ink)]">
            <div className="text-kicker mb-3">Embeds</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-kicker block mb-1">Spotify Episode ID</label>
                <input className="field font-mono-ed text-sm" value={form.spotify_episode_id} onChange={set("spotify_episode_id")}
                       placeholder="4rOoJ6Egrf8K2IrywzwOMk"
                       data-testid="episode-spotify-input" />
                <p className="text-[0.65rem] text-[var(--muted)] font-mono-ed mt-1">
                  From the URL: open.spotify.com/episode/<strong>THIS_PART</strong>
                </p>
              </div>
              <div>
                <label className="text-kicker block mb-1">YouTube Video ID</label>
                <input className="field font-mono-ed text-sm" value={form.youtube_video_id} onChange={set("youtube_video_id")}
                       placeholder="dQw4w9WgXcQ"
                       data-testid="episode-youtube-input" />
                <p className="text-[0.65rem] text-[var(--muted)] font-mono-ed mt-1">
                  From youtube.com/watch?v=<strong>THIS_PART</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t-2 border-[var(--ink)]">
            <div className="text-kicker mb-3">Hero Image</div>
            <HeroUploader token={token} value={form.hero_image} onChange={(url) => setForm({ ...form, hero_image: url })} />
          </div>

          <div className="pt-4 border-t-2 border-[var(--ink)]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-kicker">Chapters</div>
              <button type="button" onClick={addChapter}
                      className="text-xs font-display uppercase tracking-widest text-[var(--rust)] hover:underline"
                      data-testid="episode-add-chapter">
                + Add Chapter
              </button>
            </div>
            <div className="space-y-2" data-testid="episode-chapters-list">
              {form.chapters.length === 0 && (
                <p className="text-xs font-mono-ed uppercase tracking-widest text-[var(--muted)] py-4 text-center border border-dashed border-[var(--line)]">
                  No chapters yet
                </p>
              )}
              {form.chapters.map((c, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`episode-chapter-${idx}`}>
                  <GripVertical size={14} className="text-[var(--muted)]" />
                  <input className="field w-24 font-mono-ed text-sm" placeholder="m:ss"
                         value={c._time || ""} onChange={(e) => updateChapter(idx, "_time", e.target.value)} />
                  <input className="field flex-1" placeholder="Chapter label"
                         value={c.label || ""} onChange={(e) => updateChapter(idx, "label", e.target.value)} />
                  <button type="button" onClick={() => removeChapter(idx)}
                          className="p-2 text-[var(--muted)] hover:text-red-700">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t-2 border-[var(--ink)]">
            <div className="text-kicker mb-3">Guest (optional)</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-kicker block mb-1">Name</label>
                <input className="field" value={form.guest_name} onChange={set("guest_name")}
                       data-testid="episode-guest-name-input" />
              </div>
              <div>
                <label className="text-kicker block mb-1">Handle</label>
                <input className="field" value={form.guest_handle} onChange={set("guest_handle")}
                       placeholder="@handle" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-kicker block mb-1">Bio</label>
              <textarea className="field min-h-[60px] resize-y" value={form.guest_bio} onChange={set("guest_bio")} />
            </div>
          </div>

          <div className="pt-4 border-t-2 border-[var(--ink)]">
            <div className="text-kicker mb-3">Featured Articles (recaps linked to this episode)</div>
            <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto border border-[var(--line)] p-2" data-testid="episode-articles-selector">
              {articles.map((a) => (
                <label key={a.slug} className="flex items-center gap-3 p-2 text-sm hover:bg-[var(--paper-deep)]/40 cursor-pointer">
                  <input type="checkbox"
                         checked={selectedSlugs.has(a.slug)}
                         onChange={() => toggleArticle(a.slug)}
                         className="w-4 h-4 accent-[var(--rust)]" />
                  <span className="chip text-[0.6rem]">{a.category}</span>
                  <span className="flex-1 truncate">{a.title}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-kicker block mb-1">Tags (comma-separated)</label>
              <input className="field" value={form.tags} onChange={set("tags")}
                     placeholder="nfl, recap, playoffs" />
            </div>
            <label className="flex items-end gap-2 pb-2 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 accent-[var(--rust)]"
                     checked={form.featured} onChange={set("featured")}
                     data-testid="episode-featured-toggle" />
              <span className="text-kicker">Featured (Home + Top of Archive)</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-[var(--ink)]">
            <button type="submit" className="btn-rust" disabled={saving} data-testid="episode-save-button">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Publish Episode" : "Save Changes"}
            </button>
            <button type="button" onClick={onCancel} className="btn-ghost">Back</button>
          </div>
        </form>

        <aside className="lg:col-span-2">
          <div className="border-2 border-[var(--ink)] bg-[var(--paper)]">
            <div className="aspect-[16/10] bg-[var(--ink)] overflow-hidden">
              {form.hero_image ? (
                <img src={form.hero_image} alt="" className="w-full h-full object-cover"
                     onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--paper)] text-xs uppercase tracking-widest font-mono-ed">
                  hero preview
                </div>
              )}
            </div>
            <div className="p-5">
              <span className="tape">Ep. {form.number}</span>
              <h3 className="text-headline text-2xl mt-3">{form.title || "Untitled episode"}</h3>
              <p className="text-dek mt-2 text-sm line-clamp-3">{form.description || "Your description shows here…"}</p>
              <div className="text-xs font-mono-ed uppercase tracking-widest text-[var(--muted)] mt-3">
                {form.duration || "—"} · {form.chapters.filter((c) => c.label).length} chapters
              </div>
              <div className="flex gap-2 mt-3">
                {form.spotify_episode_id && <span className="chip text-[0.6rem]">Spotify</span>}
                {form.youtube_video_id && <span className="chip text-[0.6rem]">YouTube</span>}
              </div>
            </div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-3 font-mono-ed uppercase tracking-widest">
            Live preview
          </p>
        </aside>
      </div>
    </div>
  );
}

function HeroUploader({ token, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState("upload");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image files only."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB."); return; }
    setUploading(true); setProgress(0);
    try {
      const res = await adminUploadImage(token, file, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      onChange(res.url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="episode-hero-uploader">
      <div className="flex gap-2 mb-3">
        <button type="button" onClick={() => setMode("upload")}
                className={`chip ${mode === "upload" ? "chip-active" : ""}`}>
          <Upload size={12} className="inline mr-1" /> Upload
        </button>
        <button type="button" onClick={() => setMode("url")}
                className={`chip ${mode === "url" ? "chip-active" : ""}`}>
          Paste URL
        </button>
      </div>

      {mode === "upload" ? (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`block cursor-pointer border-2 border-dashed transition ${dragOver ? "border-[var(--rust)] bg-[var(--rust)]/5" : "border-[var(--ink)]"} p-6 text-center`}>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                 className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}
                 data-testid="episode-hero-file-input" />
          {uploading ? (
            <div>
              <div className="font-display uppercase tracking-widest text-sm text-[var(--rust)]">Uploading…</div>
              <div className="mt-2 h-2 bg-[var(--paper-deep)] overflow-hidden">
                <div className="h-full bg-[var(--rust)] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-xs font-mono-ed mt-2">{progress}%</div>
            </div>
          ) : (
            <div>
              <ImagePlus size={24} className="mx-auto text-[var(--rust)]" />
              <div className="font-display uppercase tracking-widest text-xs mt-2">Drop image or click</div>
              <div className="text-[0.65rem] text-[var(--muted)] mt-1 font-mono-ed">JPG · PNG · WebP · max 10 MB</div>
            </div>
          )}
        </label>
      ) : (
        <input type="url" className="field" value={value} onChange={(e) => onChange(e.target.value)}
               placeholder="https://..." />
      )}

      {value && (
        <div className="mt-3 relative">
          <img src={value} alt="" className="w-full max-h-40 object-cover border-2 border-[var(--ink)]" />
          <button type="button" onClick={() => onChange("")}
                  className="absolute top-2 right-2 bg-[var(--ink)] text-[var(--paper)] p-1.5 hover:bg-[var(--rust)]">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
